/* ---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { __generator } from "tslib";
var Node = /** @class */ (function () {
    function Node(element) {
        this.element = element;
        this.next = Node.Undefined;
        this.prev = Node.Undefined;
    }
    Node.Undefined = new Node(undefined);
    return Node;
}());
var LinkedList = /** @class */ (function () {
    function LinkedList() {
        this.first = Node.Undefined;
        this.last = Node.Undefined;
        this.mSize = 0;
    }
    Object.defineProperty(LinkedList.prototype, "size", {
        get: function () {
            return this.mSize;
        },
        enumerable: false,
        configurable: true
    });
    LinkedList.prototype.isEmpty = function () {
        return this.first === Node.Undefined;
    };
    LinkedList.prototype.clear = function () {
        this.first = Node.Undefined;
        this.last = Node.Undefined;
        this.mSize = 0;
    };
    LinkedList.prototype.unshift = function (element) {
        return this.insert(element, false);
    };
    LinkedList.prototype.push = function (element) {
        return this.insert(element, true);
    };
    LinkedList.prototype.shift = function () {
        if (this.first === Node.Undefined) {
            return undefined;
        }
        var res = this.first.element;
        this.remove(this.first);
        return res;
    };
    LinkedList.prototype.pop = function () {
        if (this.last === Node.Undefined) {
            return undefined;
        }
        var res = this.last.element;
        this.remove(this.last);
        return res;
    };
    LinkedList.prototype[Symbol.iterator] = function () {
        var node;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    node = this.first;
                    _a.label = 1;
                case 1:
                    if (!(node !== Node.Undefined)) return [3 /*break*/, 3];
                    return [4 /*yield*/, node.element];
                case 2:
                    _a.sent();
                    node = node.next;
                    return [3 /*break*/, 1];
                case 3: return [2 /*return*/];
            }
        });
    };
    LinkedList.prototype.insert = function (element, atTheEnd) {
        var _this = this;
        var newNode = new Node(element);
        if (this.first === Node.Undefined) {
            this.first = newNode;
            this.last = newNode;
        }
        else if (atTheEnd) {
            // push
            var oldLast = this.last;
            this.last = newNode;
            newNode.prev = oldLast;
            oldLast.next = newNode;
        }
        else {
            // unshift
            var oldFirst = this.first;
            this.first = newNode;
            newNode.next = oldFirst;
            oldFirst.prev = newNode;
        }
        this.mSize += 1;
        var didRemove = false;
        return function () {
            if (!didRemove) {
                didRemove = true;
                _this.remove(newNode);
            }
        };
    };
    LinkedList.prototype.remove = function (node) {
        if (node.prev !== Node.Undefined && node.next !== Node.Undefined) {
            // middle
            var anchor = node.prev;
            anchor.next = node.next;
            node.next.prev = anchor;
            node.next = Node.Undefined;
            node.prev = Node.Undefined;
        }
        else if (node.prev === Node.Undefined && node.next === Node.Undefined) {
            // only node
            this.first = Node.Undefined;
            this.last = Node.Undefined;
        }
        else if (node.next === Node.Undefined) {
            // last
            this.last = this.last.prev;
            this.last.next = Node.Undefined;
            node.prev = Node.Undefined;
        }
        else if (node.prev === Node.Undefined) {
            // first
            this.first = this.first.next;
            this.first.prev = Node.Undefined;
            node.next = Node.Undefined;
        }
        // done
        this.mSize -= 1;
    };
    return LinkedList;
}());
export { LinkedList };
